import React, { Component } from "react";
import PropTypes from "prop-types";
import NumberFormat from "react-number-format";
import Row from "../../shared_modules/common/grid/Row";
import Col from "../../shared_modules/common/grid/Col";
import ValidationMsg from "../../shared_modules/common/forms/ValidationMsg";
import {
  isLeapYear,
  differenceInCalendarYears,
  getDate,
  getMonth,
} from "date-fns";

class DateOfBirth extends Component {
  constructor(props) {
    super(props);

    this.state = {
      msgData: {
        errors: { dob: {} },
        info: { dob: {} },
      },
      dob: this.props.fieldMetadata.value || "",
    };

    this.maxAge = 130; // in years
    this.minAge = 18; // in years
    this.currentDate = new Date();
    this.leapYear = false;
  }

  componentDidMount = () => {
    setTimeout(() => {
      if (this.state.dob) {
        const e = { target: { value: this.state.dob, name: "dob" } };
        this.handleBlur(e);
      }
    }, 100);
  };

  static defaultProps = {
    element: {
      name: "dob",
      prefix: "pf-contact-",
      id: "pf-contact-dob",
      label: "Date of Birth",
      placeHolder: "MM / DD / YYYY",
      colLabelClassName: "label",
      colFieldClassName: "field",
      colValidationClassName: "messaging",
    },
    colLayout: {
      1: { sm: 12, lg: 3 },
      2: { sm: 12, lg: 3 },
      3: { sm: 12, lg: 6 },
    },
  };

  initMsgObj = () => {
    const { element } = this.props,
      msgData = {
        errors: {},
        info: {},
      };

    msgData.errors[element.name] = {};
    msgData.info[element.name] = {};

    return msgData;
  };

  generateMessage = (msgType, customMsg = "") => {
    const { element } = this.props,
      key = msgType === "error" ? "errors" : "info",
      msgData = this.initMsgObj();

    let msg = "";

    if (msgType === "info") {
      msg = "Enter your date of birth.";
    } else if (msgType === "error") {
      msg =
        "Please enter your date of birth.  You must be at least 18 years of age to apply.";
    }

    msgData[key][element.name] = {
      type: msgType,
      msg: customMsg.length > 0 ? customMsg : msg,
      id: element.id,
      slug: element.name,
    };

    return msgData;
  };

  numOfDaysForMonth = (month) => {
    const monthsWith31 = [1, 3, 5, 7, 8, 10, 12],
      monthsWith30 = [4, 6, 9, 11];

    if (month === 2) {
      return "29"; // Feb. has 28 days except for leap year, which has 29
    }

    if (monthsWith31.includes(month)) {
      return "31";
    }

    if (monthsWith30.includes(month)) {
      return "30";
    }
  };

  meetMaxAge = (
    numOfYears,
    currentMonth,
    currentDayOfMonth,
    dobMonth,
    dobDayOfMonth,
  ) => {
    let valid = true;

    if (numOfYears > this.maxAge) {
      valid = false;
    } else if (numOfYears === this.maxAge) {
      if (dobMonth < currentMonth) {
        valid = false;
      } else if (dobMonth === currentMonth) {
        if (dobDayOfMonth < currentDayOfMonth) {
          valid = false;
        }
      }
    }

    return valid;
  };

  meetMinAge = (
    numOfYears,
    currentMonth,
    currentDayOfMonth,
    dobMonth,
    dobDayOfMonth,
  ) => {
    let valid = true;

    if (numOfYears < this.minAge) {
      valid = false;
    } else if (numOfYears === this.minAge) {
      if (dobMonth > currentMonth) {
        valid = false;
      } else if (dobMonth === currentMonth) {
        if (dobDayOfMonth > currentDayOfMonth) {
          valid = false;
        }
      }
    }

    return valid;
  };

  ageEligibility = (dateStr) => {
    // applicant age is between minAge and maxAge inclusive
    const dtDOB = new Date(dateStr),
      numOfYears = differenceInCalendarYears(this.currentDate, dtDOB),
      currentMonth = getMonth(this.currentDate),
      currentDayOfMonth = getDate(this.currentDate),
      dobMonth = getMonth(dtDOB),
      dobDayOfMonth = getDate(dtDOB);

    let msg = "";

    if (
      !this.meetMinAge(
        numOfYears,
        currentMonth,
        currentDayOfMonth,
        dobMonth,
        dobDayOfMonth,
      )
    ) {
      msg = `Applicant needs to be at least ${this.minAge} years of age.`;
    } else if (
      !this.meetMaxAge(
        numOfYears,
        currentMonth,
        currentDayOfMonth,
        dobMonth,
        dobDayOfMonth,
      )
    ) {
      msg = `Applicant is over ${this.maxAge} years of age.`;
    }

    return msg;
  };

  checkUpperLimit = (val, max) => {
    if (val.length === 1 && val[0] > max[0]) {
      val = "0" + val;
    }

    if (val.length === 2) {
      if (Number(val) === 0) {
        val = "01";

        // occurs when user paste number
      } else if (val > max) {
        val = max;
      }
    }

    return val;
  };

  verifyYear = (year) => {
    const currentYear = this.currentDate.getFullYear(),
      yearLength = year.length;

    let compareNum = 0;

    switch (yearLength) {
      case 1:
        compareNum = Math.floor(currentYear / 1000);
        break;
      case 2:
        compareNum = Math.floor(currentYear / 100);
        break;
      case 3:
        compareNum = Math.floor(currentYear / 10);
        break;
      default:
        compareNum = currentYear;
        break;
    }

    if (parseInt(year, 10) > compareNum) {
      return year.substr(0, yearLength - 1);
    }

    return year;
  };

  verifyDate = (val) => {
    const numOfMonths = "12",
      month = this.checkUpperLimit(val.substring(0, 2), numOfMonths),
      numOfDays = this.numOfDaysForMonth(parseInt(month, 10)),
      day = this.checkUpperLimit(val.substring(2, 4), numOfDays),
      year = this.verifyYear(val.substring(4));

    return (
      month + (day.length ? "/" + day : "") + (year.length ? "/" + year : "")
    );
  };

  isValidDOB = (value) => {
    const validLength = value.length === 10 ? true : false,
      validMonth = value.indexOf("M") === -1 ? true : false,
      validDay = value.indexOf("D") === -1 ? true : false,
      validYear = value.indexOf("Y") === -1 ? true : false;

    return validLength && validMonth && validDay && validYear;
  };

  handleChange = (ev) => {
    this.setState({
      dob: ev.target.value,
    });
  };

  handleBlur = (ev) => {
    const { onEventCallback, fieldMetadata } = this.props,
      dob = ev.target.value,
      validDOB = this.isValidDOB(dob);

    let msgData = this.initMsgObj(),
      customMsg = "",
      isValid = false;

    if (!validDOB) {
      msgData = this.generateMessage("error");
    } else {
      const dateParts = dob.split("/");
      if (dateParts[1] === "29" && dateParts[0] === "02") {
        if (
          !isLeapYear(
            new Date(parseInt(dateParts[2], 10), 1, parseInt(dateParts[1], 10)),
          )
        ) {
          customMsg = `Invalid Date. ${dob} is not a leap year.`;
          msgData = this.generateMessage("error", customMsg);
        } else {
          // valid leap year
          this.leapYear = true;
          customMsg = this.ageEligibility(dob);
          if (customMsg.length > 0) {
            msgData = this.generateMessage("error", customMsg);
          } else {
            isValid = true;
          }
        }
      } else {
        const month = parseInt(dateParts[0], 10),
          day = parseInt(dateParts[1], 10),
          year = parseInt(dateParts[2], 10);

        if (
          month === 0 ||
          month > 12 ||
          day === 0 ||
          day > 31 ||
          year === 0 ||
          year > this.currentDate.getFullYear()
        ) {
          customMsg = "Invalid Date.";
          msgData = this.generateMessage("error", customMsg);
        } else {
          // chk valid age
          const numOfDays = this.numOfDaysForMonth(month);

          if (day > numOfDays) {
            customMsg = "Invalid Date.";
            msgData = this.generateMessage("error", customMsg);
          } else {
            customMsg = this.ageEligibility(dob);
            if (customMsg.length > 0) {
              msgData = this.generateMessage("error", customMsg);
            } else {
              isValid = true;
            }
          }
        }
      }
    }

    fieldMetadata.value = dob;
    fieldMetadata.isValid = isValid;
    if (isValid) {
      fieldMetadata.status = "";
    } else {
      fieldMetadata.status = "invalid";
    }

    this.setState({
      msgData,
      dob,
    });

    if (onEventCallback && typeof onEventCallback === "function") {
      onEventCallback(ev, fieldMetadata);
    }
  };

  handleFocus = (ev) => {
    const { fieldMetadata } = this.props;

    this.leapYear = false;
    fieldMetadata.status = "";
    this.setState({
      msgData: this.generateMessage("info"),
    });
  };

  render() {
    const { colLayout, element, fieldMetadata } = this.props;
    let { msgData } = this.state;

    if (
      fieldMetadata.status === "invalid" &&
      fieldMetadata.value.length === 0
    ) {
      const msg = "Date of birth is required.";
      msgData = this.generateMessage("error", msg);
    }

    return (
      <Row className={element.id}>
        <Col className={element.colLabelClassName} width={colLayout[1]}>
          <label htmlFor={element.id}>{element.label}</label>
        </Col>
        <Col className={element.colFieldClassName} width={colLayout[2]}>
          <NumberFormat
            key={fieldMetadata.value}
            id={element.id}
            name={element.name}
            className={fieldMetadata.status}
            type="tel"
            format="##/##/####"
            mask={["M", "M", "D", "D", "Y", "Y", "Y", "Y"]}
            placeholder={element.placeHolder}
            onChange={this.handleChange}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            defaultValue={fieldMetadata.value}
            autoComplete={fieldMetadata.autocomplete || null}
          />
        </Col>
        <Col className={element.colValidationClassName} width={colLayout[3]}>
          <ValidationMsg
            who={element.name}
            filterFields={[element.name]}
            msgs={msgData}
          />
        </Col>
      </Row>
    );
  }
}

DateOfBirth.propTypes = {
  colLayout: PropTypes.object.isRequired,
  element: PropTypes.object.isRequired,
  onEventCallback: PropTypes.func,
  fieldMetadata: PropTypes.object.isRequired,
};

export default DateOfBirth;

require('/var/lib/jenkins/workspace/pf-lcps-ui_Release_0/src/pages/borrower-registration/styles/borrower-registration.scss');